import { css } from '@emotion/react'
import React from 'react'
import type { CSSInterpolation } from '@emotion/css/create-instance'

import { AchromaticThemeOverride } from './AchromaticThemeOverride'
import { Checkbox } from './Checkbox'
import { RadioButtonGroup, StandaloneRadioButton, useRadioButtonGroupContext } from './RadioButton'
import {
  RowUtils,
  type StartElemIconProps,
  type StartElemImageProps,
  type StartElemNoneProps,
  type StartElemNumberPogProps,
  type StartElemPogProps,
} from './Rows/RowUtils'
import { Toggle } from './Toggle'
import { Type } from './Type'
import { useUniqueId } from './useUniqueId'

const styles = {
  sectionRoot: css({
    paddingBottom: '12px',
    paddingTop: '12px',
  }),
}

type Props = (
  | StartElemIconProps
  | StartElemPogProps
  | StartElemNumberPogProps
  | StartElemImageProps
  | StartElemNoneProps
) & {
  overrideRootStyles?: CSSInterpolation
  /**
   * Main leading text.
   */
  title: string
  /**
   * Secondary leading text.
   */
  caption?: string
} & (
    | {
        /**
         * The type of input.
         */
        variant: 'checkbox'
        isChecked: boolean
        /**
         * Strictly for checkbox and toggle types. For radio button
         * types, pass onChange into SelectionRow.RadioProvider.
         */
        onChange: React.ComponentProps<typeof Checkbox>['onChange']
        /**
         * The state of the selectionRow. "disabled" marks it and the input as disabled.
         */
        state?: 'default' | 'disabled' | false
      }
    | {
        variant: 'toggle'
        isChecked: boolean
        onChange: ({
          event,
          isChecked,
        }: {
          isChecked: boolean
          event: React.MouseEvent<HTMLButtonElement, MouseEvent>
        }) => void
        /**
         * For Toggle inputs, "loading" will show a spinner next to it.
         */
        state?: React.ComponentProps<typeof Toggle>['state']
        /**
         * Position of the spinner for the Toggle. By default it is displayed on the left side (start).
         */
        spinnerPosition?: React.ComponentProps<typeof Toggle>['spinnerPosition']
      }
    | {
        variant: 'radioButton'
        /**
         * The value of this radioButton. Selected value state must
         * be passed into a parent SelectionRow.RadioProvider component,
         * along with an onChange prop.
         */
        value: string
        /**
         * The state of the selectionRow. "disabled" marks it and the input as disabled.
         */
        state?: 'default' | 'disabled' | false
      }
  )

// NOTE(omer.demirkan): To avoid reinventing the wheel, this
// is using the internal radio button group context provider.
// The StandaloneRadioButton component already notifies the
// user if they use SelectionRow outside this context.
export const SelectionRowRadioGroupProvider = RadioButtonGroup

export function SelectionRow({ caption, title, ...restProps }: Props) {
  const { colors } = RowUtils.useRowColors(false)
  const inputId = useUniqueId()
  const { onChange } = useRadioButtonGroupContext()

  return (
    <RowUtils.Container
      isDisabled={restProps.state === 'disabled'}
      isInteractive
      isTabbable
      // NOTE (szhang): Adding synthentic event to make the whole row clickable
      onClick={() => {
        if (restProps.variant === 'checkbox' && restProps.onChange) {
          const syntheticEvent = {
            target: { value: inputId },
          } as React.ChangeEvent<HTMLInputElement>;
          restProps.onChange({
            event: syntheticEvent,
            isChecked: !restProps.isChecked,
          });
        } else if (restProps.variant === 'toggle' && restProps.onChange) {
          // NOTE (szhang): Using unknown here to make ts ignore missing properties
          const syntheticEvent = {
            target: { value: inputId },
          } as unknown as React.MouseEvent<HTMLButtonElement, MouseEvent>;
          restProps.onChange({
            event: syntheticEvent,
            isChecked: restProps.isChecked,
          });
        } else if (restProps.variant === 'radioButton' && restProps.value) {
          const syntheticEvent = {
            target: { value: restProps.value },
          } as React.ChangeEvent<HTMLInputElement>;
          onChange?.(syntheticEvent);
        }
      }}
    >
      <RowUtils.Section
        leadingContent={
          <RowUtils.LeadingContent
            isDisabled={restProps.state === 'disabled'}
            {...restProps}
          />
        }
        mainContent={
          <>
            <Type.Text
              color={colors.primaryText}
              element="span"
              maxLines={3}
              size="M"
            >
              <label htmlFor={inputId}>{title}</label>
            </Type.Text>
            {caption && (
              <Type.Text
                color={colors.secondaryText}
                element="p"
                maxLines={3}
                size="S"
              >
                {caption}
              </Type.Text>
            )}
          </>
        }
        overrideRootStyles={[styles.sectionRoot, restProps.overrideRootStyles]}
        trailingContent={
          // NOTE(omer.demirkan): The theme overrides are to align with
          // the mockups for this component using current versions
          // of inputs.
          // TODO: Remove overrides once checkbox and radiobutton
          // default colors change.
          <>
            {restProps.variant === 'checkbox' && (
              <AchromaticThemeOverride>
                {({ rootClassName }) => (
                  <span className={rootClassName}>
                    <Checkbox
                      id={inputId}
                      isChecked={restProps.isChecked}
                      onChange={restProps.onChange}
                      state={restProps.state}
                    />
                  </span>
                )}
              </AchromaticThemeOverride>
            )}
            {restProps.variant === 'radioButton' && (
              <AchromaticThemeOverride>
                {({ rootClassName }) => (
                  <span className={rootClassName}>
                    <StandaloneRadioButton
                      id={inputId}
                      state={restProps.state}
                      value={restProps.value}
                    />
                  </span>
                )}
              </AchromaticThemeOverride>
            )}
            {restProps.variant === 'toggle' && (
              <Toggle
                id={inputId}
                isChecked={restProps.isChecked}
                onChange={restProps.onChange}
                state={restProps.state}
              />
            )}
          </>
        }
      />
    </RowUtils.Container>
  )
}
